import { useMemo } from "react";
import { trpc } from "@/hooks/trpc";
import {
  AttributeKindEnum,
  TemplateAttributeRole,
  type HarvestOperationTemplateRevisionId,
  type HarvestOperationTemplateAttribute,
} from "@brewbase/db/types";
import type {
  EditorConfig,
  TabularColumnInput,
  TabularColumnRow,
  TabularColumnsApi,
} from "@/features/template-block-editor/types";
import {
  renderOperationRowFlags,
  renderOperationTabularBadge,
} from "./row-flags-operation";
import { OperationInspectorExtras } from "./inspector-extras-operation";
import { columnTextOptions } from "@brewbase/lib";
import { toast } from "sonner";

// Editor config for CORRECTION mode: reads reuse the existing revision-scoped
// queries (attributeList / templateTabularColumns.list point at the working
// revision W), writes go through harvest.templateCorrections.* — which are
// lineage-preserving and side-effect-free (no variant dirty / protocol sync).

function useCorrectionTabularColumnsApi(
  workingId: HarvestOperationTemplateRevisionId,
  templateAttributeId: number,
): TabularColumnsApi {
  const utils = trpc.useUtils();
  const attrId = templateAttributeId as never;

  const { data: columns, isLoading } =
    trpc.harvest.templateTabularColumns.list.useQuery(
      { templateAttributeId: attrId },
      { enabled: !!templateAttributeId },
    );

  const setColumnsMutation = trpc.harvest.templateCorrections.setColumns.useMutation({
    // Column edits are written to the cache before saving, so a refused save
    // must say so: the refetch would otherwise silently undo the edit.
    onError: (err) => toast.error(`Column not saved: ${err.message}`),
    onSettled: () =>
      utils.harvest.templateTabularColumns.list.invalidate({ templateAttributeId: attrId }),
  });

  const replaceColumns = (cols: TabularColumnInput[]) =>
    setColumnsMutation.mutate({
      workingId,
      attrId: templateAttributeId,
      columns: cols.map((c) => ({
        // Preserve identity for existing columns; null = new (mint lineage).
        lineageId: c.lineage_id != null ? String(c.lineage_id) : null,
        name: c.name,
        code: c.code,
        dataType: c.data_type as "number" | "string" | "datetime",
        isIndex: c.is_index,
        isComputed: c.is_computed,
        formula: c.formula,
        unit: c.unit,
        sortOrder: c.sort_order,
        textOptions: columnTextOptions(c),
      })),
    });

  const setColumnsCache = (cols: TabularColumnRow[]) =>
    utils.harvest.templateTabularColumns.list.setData(
      { templateAttributeId: attrId },
      cols as unknown as NonNullable<typeof columns>,
    );

  return { columns, isLoading, replaceColumns, setColumnsCache };
}

export function useOperationCorrectionEditorConfig(
  workingId: HarvestOperationTemplateRevisionId,
): EditorConfig {
  const utils = trpc.useUtils();

  const { data: revision } =
    trpc.harvest.operationTemplates.revisionGetById.useQuery(workingId);
  const { data: attributesData } =
    trpc.harvest.operationTemplates.attributeList.useQuery(workingId);

  const invalidate = () =>
    utils.harvest.operationTemplates.attributeList.invalidate(workingId);

  const addAttr = trpc.harvest.templateCorrections.addAttribute.useMutation({ onSettled: invalidate });
  const updateAttr = trpc.harvest.templateCorrections.updateAttribute.useMutation({ onSettled: invalidate });
  const removeAttr = trpc.harvest.templateCorrections.removeAttribute.useMutation({ onSettled: invalidate });
  const reorderAttr = trpc.harvest.templateCorrections.reorderAttributes.useMutation({ onSettled: invalidate });

  const availableBatchTypes = revision?.output_materials ?? [];

  const attributes = useMemo(
    () =>
      [...(attributesData ?? [])].sort((a, b) => {
        const roleA = a.role === TemplateAttributeRole.PARAMETER ? 0 : 1;
        const roleB = b.role === TemplateAttributeRole.PARAMETER ? 0 : 1;
        if (roleA !== roleB) return roleA - roleB;
        return (a.display_order ?? 0) - (b.display_order ?? 0);
      }),
    [attributesData],
  );

  return useMemo<EditorConfig>(
    () => ({
      templateId: workingId,
      isLocked: false,
      attributes,
      addAttribute: async (kind: AttributeKindEnum, section) => {
        const id = await addAttr.mutateAsync({ workingId, kind });
        const role = section?.role;
        if (role && role !== TemplateAttributeRole.RECORDING) {
          await updateAttr.mutateAsync({ workingId, attrId: id, settings: { role } });
        }
        return id;
      },
      removeAttribute: (id) => removeAttr.mutate({ workingId, attrId: id }),
      reorderAttributes: (ids) => reorderAttr.mutate({ workingId, attrIds: ids }),
      updateAttribute: (id, settings) =>
        updateAttr.mutate({ workingId, attrId: id, settings: normalize(settings) }),
      updateAttributeType: (id, updates) =>
        updateAttr.mutate({ workingId, attrId: id, settings: updates }),
      sections: [
        {
          title: "Parameters",
          description: "Operational settings with default values.",
          role: TemplateAttributeRole.PARAMETER,
          scalarInsertOnly: true,
          select: (all) =>
            all.filter((a) => (a as HarvestOperationTemplateAttribute).role === TemplateAttributeRole.PARAMETER),
        },
        {
          title: "Attributes",
          description: "Data recorded during operation execution.",
          role: TemplateAttributeRole.RECORDING,
          select: (all) =>
            all.filter((a) => (a as HarvestOperationTemplateAttribute).role === TemplateAttributeRole.RECORDING),
        },
      ],
      formulaContextKind: "harvest-attribute",
      tabularColumnFormulaContextKind: "harvest-tabular-column",
      useTabularColumnsApi: (templateAttributeId: number) =>
        useCorrectionTabularColumnsApi(workingId, templateAttributeId),
      renderRowFlags: renderOperationRowFlags,
      renderTabularBadge: renderOperationTabularBadge,
      renderInspectorExtras: (args) => (
        <OperationInspectorExtras {...args} availableBatchTypes={availableBatchTypes} />
      ),
    }),
    [workingId, attributes, addAttr, updateAttr, removeAttr, reorderAttr, availableBatchTypes],
  );
}

function normalize(settings: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...settings };
  for (const [k, v] of Object.entries(out)) {
    if (typeof v === "string" && v.trim() === "") out[k] = null;
  }
  return out;
}
